import { useWSModal, WSList, WSLoader } from "@wingspanhq/fe-component-library";
import { Card, CardType } from "@wingspanhq/wingspan-api-sdk";
import React, { useEffect, useMemo, useRef } from "react";
import { queriesCards } from "@query";
import { ErrorAlert } from "@components/ErrorAlert";
import { useQueryPrimaryComplianceEntity } from "../../../../shared/hooks/useQueryPrimaryComplianceEntity";
import { selectorCardShippingAddress } from "../../selectors/selectorCardShippingAddress";

const INVALID_ADDRESS_ERROR =
  "We don't have a valid address on file for you. Please update your address in settings and try again.";

type CreateVirtualCardModalProps = {
  onClose: () => void;
  onSuccess: (card: Card) => void;
};

const CreateVirtualCardModal: React.FC<CreateVirtualCardModalProps> = props => {
  const queryPrimaryComplianceEntity = useQueryPrimaryComplianceEntity();
  const [createCard, createCardMeta] = queriesCards.createCard.useMutation();
  const isCardRequested = useRef(false);

  const complianceEntity = queryPrimaryComplianceEntity.data;
  const shippingAddress = useMemo(
    () => selectorCardShippingAddress(complianceEntity?.physicalAddress),
    [complianceEntity]
  );

  useEffect(() => {
    if (!shippingAddress || isCardRequested.current) {
      return;
    }

    isCardRequested.current = true;

    createCard(
      {
        cardCreate: {
          cardType: CardType.VIRTUAL,
          shippingAddress
        }
      },
      {
        onSuccess: card => {
          props.onSuccess(card);
          props.onClose();
        }
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingAddress]);

  const error =
    queryPrimaryComplianceEntity.error ||
    createCardMeta.error ||
    (queryPrimaryComplianceEntity.isSuccess && !shippingAddress
      ? INVALID_ADDRESS_ERROR
      : undefined);

  return (
    <WSList gap="L">
      {error ? (
        <ErrorAlert mb="XL" error={error} context="BankCard" />
      ) : (
        <WSLoader.Spinner />
      )}
    </WSList>
  );
};

export const useCreateVirtualCardModal = () =>
  useWSModal(CreateVirtualCardModal, {
    title: "Activate virtual card",
    size: "S"
  });
